import ky, {
  isHTTPError,
  isKyError,
  SchemaValidationError,
  type Options,
} from "ky"

import { parseJSON } from "./json"

const HTTP_STATUS_UNAUTHORIZED = 401

export class ResponseError extends Error {
  error: string
  statusCode: number
  detail: string
  title: string
  traceId: string

  constructor({
    error,
    statusCode,
    detail,
    message,
    title,
    traceId,
  }: {
    error: string
    statusCode: number
    message: string

    detail: string
    title: string
    traceId: string
  }) {
    super(message)
    this.error = error
    this.statusCode = statusCode
    this.detail = detail
    this.title = title
    this.traceId = traceId
  }
}

const _request = ky.extend({
  parseJson: parseJSON,
  hooks: {
    afterResponse: [
      async ({ request, response, retryCount }) => {
        if (response.status === HTTP_STATUS_UNAUTHORIZED && retryCount === 0) {
          // Only refresh on first 401, not on subsequent retries
          await ky.post("/auth/refresh").catch(() => {
            // oxlint-disable-next-line eslint/no-warning-comments
            //TODO: do some testing
            //handle in react query?
            return Response.json(
              {
                title: "refresh failed",
                detail: "refresh failed",
              },
              {
                status: HTTP_STATUS_UNAUTHORIZED,
                statusText: "refresh failed",
              },
            )
          })

          return ky.retry({
            request: new Request(request),
            code: "TOKEN_REFRESHED",
          })
        }
        return undefined
      },
    ],
  },
})

export const errorFormatter = async <T>(val: Promise<T>): Promise<T> => {
  try {
    return await val
  } catch (error) {
    if (
      isHTTPError<{
        detail: string
        status: number
        title: string
        traceId: string
        type: string
      }>(error)
    ) {
      const { response, data: body } = error
      // oxlint-disable-next-line unicorn/prefer-ternary
      if (typeof body === "object") {
        throw new ResponseError({
          error: response.statusText,
          statusCode: response.status,
          message: `${response.statusText}: ${body.title} ${body.detail}`,
          detail: body.detail,
          title: body.title,
          traceId: body.traceId,
        })
      } else {
        throw new ResponseError({
          error: "Unknown Error",
          statusCode: 500,
          // oxlint-disable-next-line typescript/prefer-nullish-coalescing if body is empty, it should show "Unknown Error"
          message: body || "Unknown Error",
          title: "Unknown Error",
          detail: "Unknown Error",
          traceId: "",
        })
      }
    } else if (isKyError(error)) {
      const { message, name } = error
      throw new ResponseError({
        error: name,
        statusCode: 0,
        message: `${name}: ${message}`,
        title: name,
        detail: message,
        traceId: "",
      })
    } else if (error instanceof SchemaValidationError) {
      throw new ResponseError({
        error: "Validation Error",
        statusCode: 0,
        message: error.message,
        title: error.name,
        detail: error.issues.map((i) => i.message).join(", "),
        traceId: "",
      })
    }

    throw new ResponseError({
      error: "Unknown Error",
      statusCode: 0,
      message: String(error),
      title: "Unknown Error",
      detail: "Unknown Error",
      traceId: "",
    })
  }
}

export const request = {
  get: <T>(url: string, opts?: Options): Promise<T> =>
    errorFormatter(_request.get(url, opts).json<T>()),
  post: <T>(url: string, opts?: Options): Promise<T> =>
    errorFormatter(_request.post(url, opts).json<T>()),
  put: <T>(url: string, opts?: Options): Promise<T> =>
    errorFormatter(_request.put(url, opts).json<T>()),
  patch: <T>(url: string, opts?: Options): Promise<T> =>
    errorFormatter(_request.patch(url, opts).json<T>()),
  delete: <T>(url: string, opts?: Options): Promise<T> =>
    errorFormatter(_request.delete(url, opts).json<T>()),
  request: _request,
}
